import { useState, type FormEvent } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import { Page } from '@/components/Page'
import { formatKarachiDateTime } from '@/lib/dates'
import { formatPKR, pkrToPaisa, sumPaisa } from '@/lib/money'
import { openWorkspaceDb } from '@/offline/db'
import { getActiveWorkspaceId } from '@/features/workspace/workspace'
import { saleOutstanding } from './outstanding'
import { buildPayment } from './paymentPayload'
import { savePayment } from './savePayment'

const inputCls =
  'w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:border-sky-500 focus:outline-none'

/** Udhaar / receivables: who owes me today, receive partial payments, recent receipts. */
export function ReceivablesPage() {
  const ws = getActiveWorkspaceId()

  const sales = useLiveQuery(
    async () => (ws ? openWorkspaceDb(ws).sales.where('workspace_id').equals(ws).toArray() : []),
    [ws],
  )
  const payments = useLiveQuery(
    async () => (ws ? openWorkspaceDb(ws).payments.where('workspace_id').equals(ws).toArray() : []),
    [ws],
  )
  const devices = useLiveQuery(
    async () => (ws ? openWorkspaceDb(ws).devices.where('workspace_id').equals(ws).toArray() : []),
    [ws],
  )
  const contacts = useLiveQuery(
    async () => (ws ? openWorkspaceDb(ws).contacts.where('workspace_id').equals(ws).toArray() : []),
    [ws],
  )

  const [openSaleId, setOpenSaleId] = useState<string | null>(null)
  const [amount, setAmount] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const liveSales = (sales ?? []).filter((s) => !s.deleted_at && !s.returned_at)
  const owed = liveSales
    .map((s) => ({ sale: s, outstanding: saleOutstanding(s, payments ?? []) }))
    .filter((x) => x.outstanding > 0)
    .sort((a, b) => b.sale.sold_at.localeCompare(a.sale.sold_at))
  const totalOwed = sumPaisa(owed.map((x) => x.outstanding))

  const modelName = (deviceId: string) => devices?.find((d) => d.id === deviceId)?.model ?? 'Device'
  const saleModel = (saleId: string) => {
    const s = sales?.find((x) => x.id === saleId)
    return s ? modelName(s.device_id) : 'Sale'
  }
  const customerName = (contactId: string | null) =>
    contactId ? (contacts?.find((c) => c.id === contactId)?.name ?? 'Customer') : 'Walk-in'

  const recentPayments = (payments ?? [])
    .filter((p) => !p.deleted_at && p.direction === 'in' && p.related_type === 'sale')
    .sort((a, b) => b.paid_at.localeCompare(a.paid_at))
    .slice(0, 8)

  async function onReceive(e: FormEvent, saleId: string, outstanding: number) {
    e.preventDefault()
    setError(null)
    if (!ws) return
    let paisa: number
    try {
      paisa = pkrToPaisa(amount)
    } catch {
      return setError('Enter a valid amount.')
    }
    if (paisa <= 0) return setError('Amount must be positive.')
    if (paisa > outstanding) return setError(`Cannot exceed outstanding ${formatPKR(outstanding)}.`)

    setBusy(true)
    try {
      const summary = await savePayment(ws, buildPayment({ workspace_id: ws, sale_id: saleId, amount_paisa: paisa }))
      if (summary.rejected.length > 0) {
        const fe = summary.rejected[0].result.field_errors?.[0]
        setError(`Not saved: ${summary.rejected[0].result.status}${fe ? ` — ${fe.message}` : ''}`)
        return
      }
      setOpenSaleId(null)
      setAmount('')
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Page title="Udhaar / Receivables" note="Running balance per sale; receive partial payments.">
      <div className="mb-3 rounded-xl border border-slate-800 bg-slate-900/40 px-3 py-3">
        <p className="text-[11px] uppercase tracking-wide text-slate-500">Total owed to you</p>
        <p className={`mt-1 text-2xl font-semibold tabular-nums ${totalOwed > 0 ? 'text-amber-400' : 'text-emerald-400'}`}>
          {formatPKR(totalOwed)}
        </p>
      </div>

      {error ? (
        <p role="alert" className="mb-3 rounded-lg border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-sm text-rose-300">
          {error}
        </p>
      ) : null}

      {owed.length === 0 ? (
        <p className="rounded-lg border border-dashed border-slate-700 px-4 py-8 text-center text-sm text-slate-500">
          No outstanding udhaar. Everyone has paid in full.
        </p>
      ) : (
        <ul className="space-y-2">
          {owed.map(({ sale, outstanding }) => (
            <li key={sale.id} className="rounded-lg border border-slate-800 bg-slate-900/40 px-3 py-2.5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">{modelName(sale.device_id)}</p>
                  <p className="text-xs text-slate-400">
                    {customerName(sale.customer_contact_id)} • {formatKarachiDateTime(sale.sold_at)}
                  </p>
                </div>
                <span className="text-sm font-semibold tabular-nums text-amber-400">{formatPKR(outstanding)}</span>
              </div>
              {openSaleId === sale.id ? (
                <form onSubmit={(e) => void onReceive(e, sale.id, outstanding)} className="mt-2 flex gap-2">
                  <input
                    className={inputCls}
                    inputMode="decimal"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder={`max ${formatPKR(outstanding, { symbol: false })}`}
                    autoFocus
                  />
                  <button type="submit" disabled={busy} className="shrink-0 rounded-lg bg-emerald-500 px-3 py-2 text-sm font-semibold text-white disabled:opacity-60">
                    {busy ? '…' : 'Receive'}
                  </button>
                </form>
              ) : (
                <div className="mt-2 flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setOpenSaleId(sale.id)
                      setAmount('')
                      setError(null)
                    }}
                    className="rounded-lg bg-sky-500/15 px-3 py-1 text-xs font-semibold text-sky-400"
                  >
                    Receive payment
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {recentPayments.length > 0 ? (
        <div className="mt-4">
          <h2 className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">Recent receipts</h2>
          <ul className="divide-y divide-slate-800 rounded-lg border border-slate-800 bg-slate-900/40">
            {recentPayments.map((p) => (
              <li key={p.id} className="flex items-center justify-between px-3 py-2 text-sm">
                <span className="text-slate-400">{saleModel(p.related_id)} • {formatKarachiDateTime(p.paid_at)}</span>
                <span className="tabular-nums text-emerald-400">+{formatPKR(p.amount_paisa)}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </Page>
  )
}
